import React, { useMemo, forwardRef, useRef, useImperativeHandle, useCallback } from 'react';
import {
  TablePaginatedV8,
  order2Sorting,
  TableCellV8,
  CellCenterV8,
  CellDateTimeV8,
  GridLinkButton,
} from 'motion-components';
import { generatePath, useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';

import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import { gridInactiveRowStyle, gridRowSelectedStyle } from 'shared-pages/common/grid-helpfunc';
import { getRouteByName } from 'app/routes';
import { FaCheckCircle, FaEdit, FaArrowsAlt } from 'react-icons/fa';
import { AlarmTypeValues } from 'shared-pages/shared/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip } from 'react-bootstrap';

/* ----------- Grid ------------------ */

export const DataGrid = forwardRef((props: any, ref) => {
  const {
    data,
    dataCount,
    initPageIndex,
    onPageChange,
    loading,
    handleExport,
    initOrderBy,
    onOrderChange,
    selected,
    setSelected,
    refetch,
    isGridInForm,
  } = props;
  const location = useLocation<any>();
  const history = useHistory();
  const { appStore, tableStore } = useRootStore();
  const { t, i18n } = useTranslation();

  const rowClicked = useCallback(
    (row) => {
      history.push({
        pathname: generatePath(getRouteByName('Idoc'), { id: row.original.idocId }),
        state: {
          // pathname: location.pathname,
          // ...location.state,
        },
      });
    },
    [history, location.pathname, location.state],
  );

  const actionClicked = useCallback(
    (row) => {
      history.push({
        pathname: row.link,
        state: {
          // pathname: location.pathname,
          // ...location.state,
        },
      });
    },
    [history, location.pathname, location.state],
  );

  function getApprovalStatus(row: any): boolean {
    switch (row.original.alarmTypeId) {
      case AlarmTypeValues.CollectionVehicle:
        return row.original.vehicle?.isApproved ?? false;
      case AlarmTypeValues.CollectionDriver:
      case AlarmTypeValues.ADR:
        return row.original.driver?.isApproved ?? false;
      case AlarmTypeValues.CollectionPermit:
      case AlarmTypeValues.CollectorInsurance:
      case AlarmTypeValues.SAMEE_Agreement:
      case AlarmTypeValues.SAMEE_Certificate:
        return row.original.collector?.isApproved ?? false;
      case AlarmTypeValues.SecondaryStorage_AEPO:
      case AlarmTypeValues.SecondaryStorage_Insurance:
      case AlarmTypeValues.RecyclingCompany_AEPO:
      case AlarmTypeValues.RecyclerInsurance:
      case AlarmTypeValues.RecyclerISO:
        return row.original.wastereceptionpoint?.isApproved ?? false;
      default:
        return false; // Default case if the ID is not recognized
    }
  }

  const columns = useMemo(
    () => [
      //EyeColumnV8(rowClicked),
      {
        id: 'id',
        header: t('ID'),
        columnName: t('ID'),
        accessorKey: 'id',
        orderByField: 'id',
        cell: CellCenterV8,
        size: 5,
      },
      {
        id: 'alarmDate',
        header: t('Ημερομηνία Alarm'),
        columnName: t('Ημερομηνία Alarm'),
        accessorKey: 'alarmDate',
        orderByField: 'alarmDate',
        cell: CellDateTimeV8,
        size: 10,
        meta: { type: 'text' },
      },
      /* {
        id: 'idocId',
        header: t('Έντυπο Αναγνώρισης'),
        columnName: t('Έντυπο Αναγνώρισης'),
        accessorKey: 'idocId',
        orderByField: 'idocId',
        //@ts-ignore
        cell: ({ row }) => (
          <div className="text-center">
            <GridLinkButton
              onClick={() => {
                rowClicked(row);
              }}
            >
              {row.original.idocId}
            </GridLinkButton>
          </div>
        ),
        size: 20,
      }, */

      {
        id: 'alarmType.name',
        header: t('Κατηγορία'),
        columnName: t('Κατηγορία'),
        accessorKey: 'alarmType.name',
        orderByField: 'alarmType.name',
        cell: TableCellV8,
        size: 20,
      },
      {
        id: 'alarmType.description',
        header: t('Μήνυμα'),
        columnName: t('Μήνυμα'),
        accessorKey: 'alarmType.description',
        orderByField: 'alarmType.description',
        cell: ({ row }) => {
          return row.original.message ? (
            <div>{row.original.message}</div>
          ) : (
            <div>{row.original.alarmType.description}</div>
          );
        },
        size: 20,
      },
      {
        id: 'info',
        header: t('Αρχείο'),
        columnName: t('Αρχείο'),
        accessorKey: 'info',
        orderByField: 'collector.name',
        cell: ({ row }) => {
          const {
            collector,
            idocId,
            idoc,
            driver,
            vehicle,
            driverId,
            vehicleId,
            wastereceptionpointId,
            wastereceptionpoint,
          } = row.original;

          return (
            <div className="flex flex-col text-center">
              {/* Collector Name */}
              {collector?.name && <div className="font-bold">{collector.name}</div>}

              {/* IDOC */}
              {idocId && (
                <div>
                  Έντυπο Αναγνώρισης:{' '}
                  <GridLinkButton onClick={() => rowClicked(row)}>
                    {idoc ? idoc.identificationDocument : idocId}
                  </GridLinkButton>
                </div>
              )}

              {/* Driver */}
              {driverId && (
                <div>
                  {t('Οδηγός:')} {driver.name}
                </div>
              )}

              {/* Vehicle */}
              {vehicleId && (
                <div>
                  {t('Όχημα:')} {vehicle.plateNumber}
                </div>
              )}

              {/* Waste Reception Point */}
              {wastereceptionpointId && (
                <div>
                  {t('Σημείο Παραλαβής:')} {wastereceptionpoint?.name}
                </div>
              )}
            </div>
          );
        },
        size: 80,
      },
      {
        id: 'link',
        header: t('Ενέργεια'),
        columnName: t('Ενέργεια'),
        accessorKey: 'link',
        orderByField: 'link',
        //@ts-ignore
        cell: ({ row }) => {
          if (!!row.original.link) {
            const isApproved = getApprovalStatus(row);
            if (isApproved === true) {
              return (
                <div
                  className="text-center"
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    actionClicked(row.original);
                  }}
                >
                  <FaCheckCircle size={30} color="#198754" />
                </div>
              );
            } else {
              return (
                <div
                  className="text-center"
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    actionClicked(row.original);
                  }}
                >
                  <FaEdit size={30} color="#ffc107" />
                </div>
              );
            }
          } else {
            if (row.original.isFixed === true) {
              return (
                <div className="text-center">
                  <FaCheckCircle size={30} color="#198754" />{' '}
                </div>
              );
            } else {
              if (row.original.idocId) {
                return (
                  <div className="text-center">
                    <GridLinkButton onClick={() => rowClicked(row)}>
                      <FaEdit size={30} color="#ffc107" />
                    </GridLinkButton>
                  </div>
                );
              } else {
                return <div></div>;
              }
            }
            // return (<div
            //   className="text-center"
            //   style={{ cursor: 'pointer' }}
            //   onClick={() => {
            //     actionClicked(row.original);
            //   }}
            //   data-toggle="tooltip" data-placement="left" title="Επανα-υπολογισμός Alarm"
            // >
            //   <svg width="26" style={{ fill: "navy" }} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M105.1 202.6c7.7-21.8 20.2-42.3 37.8-59.8c62.5-62.5 163.8-62.5 226.3 0L386.3 160 352 160c-17.7 0-32 14.3-32 32s14.3 32 32 32l111.5 0c0 0 0 0 0 0l.4 0c17.7 0 32-14.3 32-32l0-112c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 35.2L414.4 97.6c-87.5-87.5-229.3-87.5-316.8 0C73.2 122 55.6 150.7 44.8 181.4c-5.9 16.7 2.9 34.9 19.5 40.8s34.9-2.9 40.8-19.5zM39 289.3c-5 1.5-9.8 4.2-13.7 8.2c-4 4-6.7 8.8-8.1 14c-.3 1.2-.6 2.5-.8 3.8c-.3 1.7-.4 3.4-.4 5.1L16 432c0 17.7 14.3 32 32 32s32-14.3 32-32l0-35.1 17.6 17.5c0 0 0 0 0 0c87.5 87.4 229.3 87.4 316.7 0c24.4-24.4 42.1-53.1 52.9-83.8c5.9-16.7-2.9-34.9-19.5-40.8s-34.9 2.9-40.8 19.5c-7.7 21.8-20.2 42.3-37.8 59.8c-62.5 62.5-163.8 62.5-226.3 0l-.1-.1L125.6 352l34.4 0c17.7 0 32-14.3 32-32s-14.3-32-32-32L48.4 288c-1.6 0-3.2 .1-4.8 .3s-3.1 .5-4.6 1z" /></svg>
            // </div>)
          }
        },
        size: 20,
      },

      // isActiveColumn(t, i18n),
      // firstDateUserColumn(t),
      // lastDateUserColumn(t),
    ],
    [t],
  );

  const tableRef = useRef(null);
  // return of useRef
  useImperativeHandle(ref, () => (tableRef.current ? tableRef.current : null));

  return (
    <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
      {data ? (
        <TablePaginatedV8
          ref={tableRef}
          loading={loading}
          id="alarms-grid"
          appStore={appStore}
          tableStore={tableStore}
          key="grid"
          columns={columns as any}
          data={data}
          dataCount={dataCount}
          topBorderRadius={false}
          onRowClick={(table) => setSelected(table.row.original)}
          rowProps={(row: any) => {
            let style = undefined;
            if (row.original.id === selected?.id) style = gridRowSelectedStyle;
            if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };
            return { style: style };
          }}
          onExport={handleExport}
          onPageChange={onPageChange}
          initPageIndex={initPageIndex}
          initSorting={order2Sorting(initOrderBy)}
          onSortByChange={(sort) => {
            console.log('$$$$ grid sortbychange', sort);
            // $$$
            const columnId = sort[0].id;
            const sortOrder = sort[0].desc ? 'DESC' : 'ASC';
            // find column by id
            const cols = columns.filter((r: any) => r.id === columnId);
            if (cols.length === 1) {
              const col = cols[0] as any;
              let order = col.orderByField ? col.orderByField : 'id';
              order = order + ' ' + sortOrder;
              onOrderChange(order);
            }
          }}
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </LoadingOverlay>
  );
});
